import { Button } from "antd";
import { CommentOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import { useUser } from "../hooks/useUser";

// Add TypeScript declarations for chatbase and user config
declare global {
  interface Window {
    chatbase?: {
      (command: string, ...params: unknown[]): void;
      q?: unknown[][];
      getState?: () => string;
      open?: () => void;
    };
    chatbaseUserConfig?: {
      user_id: string;
      user_hash?: string;
      user_metadata: {
        name: string;
        email: string;
        [key: string]: string;
      };
    };
  }
}

const FloatingButton = styled(Button)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  border-radius: 50% !important;
  width: 48px;
  height: 48px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1050;
  min-width: 48px;
  min-height: 48px;
  line-height: 48px;

  .anticon {
    font-size: 24px;
    margin: 0;
  }

  &:hover {
    transform: scale(1.1);
  }
`;

export const ChatbaseButton = () => {
  const initialized = useRef(false);
  const { user } = useUser();
  const [userVerification, setUserVerification] = useState<{
    user_id: string;
    user_hash?: string;
    user_metadata: { [key: string]: string };
  } | null>(null);

  // Function to create a simplified version without hash validation
  const createSimpleUserConfig = () => {
    if (!user) return null;

    return {
      user_id: user.id,
      user_metadata: {
        name: user.user_metadata?.first_name
          ? `${user.user_metadata?.first_name || ""} ${
              user.user_metadata?.last_name || ""
            }`.trim()
          : (user.email || "").split("@")[0],
        email: user.email || "",
      },
    };
  };

  useEffect(() => {
    // Skip the verification process entirely and use the simplified approach
    if (user) {
      const simpleConfig = createSimpleUserConfig();
      setUserVerification(simpleConfig);
    } else {
      setUserVerification(null);
    }
  }, [user?.id]); // Only re-run when user ID changes

  useEffect(() => {
    // Only initialize Chatbase once, even if verification failed, we'll still show the button
    if (initialized.current) return;

    // If we have user info (with or without hash), initialize Chatbase
    if (user) {
      initialized.current = true;

      // Add a style to hide the default button
      const style = document.createElement("style");
      style.textContent = `
        .chatbase-bubble, .chatbase-bubble-img {
          display: none !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(style);

      // Inject the user configuration first (before the Chatbase script)
      const injectUserConfig = () => {
        // Use userVerification if available, otherwise create minimal config
        const config = userVerification || {
          user_id: user.id,
          user_metadata: {
            name: user.user_metadata?.first_name
              ? `${user.user_metadata?.first_name || ""} ${
                  user.user_metadata?.last_name || ""
                }`.trim()
              : (user.email || "").split("@")[0],
            email: user.email || "",
          },
        };

        const userConfigScript = document.createElement("script");
        userConfigScript.innerHTML = `
          window.chatbaseUserConfig = {
            user_id: "${config.user_id}",
            ${config.user_hash ? `user_hash: "${config.user_hash}",` : ""}
            user_metadata: {
              "name": "${config.user_metadata.name || ""}",
              "email": "${config.user_metadata.email || ""}"
            }
          }
        `;
        document.body.appendChild(userConfigScript);
      };

      // Inject the exact Chatbase script
      const injectScript = () => {
        // Add user config first
        injectUserConfig();

        // Then create a script element with exact code you provided
        const scriptEl = document.createElement("script");
        scriptEl.innerHTML = `
          (function(){
            // Initialize chatbase queue as an array first
            window.chatbase = window.chatbase || function() {
              window.chatbase.q = window.chatbase.q || [];
              window.chatbase.q.push(arguments);
            };
            
            // Ensure q is always initialized as an array
            window.chatbase.q = window.chatbase.q || [];
            
            if (!window.chatbase || window.chatbase("getState") !== "initialized") {
              window.chatbase = new Proxy(window.chatbase, {
                get(target, prop) {
                  if (prop === "q") {
                    return target.q || [];
                  }
                  return (...args) => target(prop, ...args);
                }
              });
            }
            
            const onLoad = function() {
              const script = document.createElement("script");
              script.src = "https://www.chatbase.co/embed.min.js";
              script.id = "y8recvp5I7IIxSzOOlhkt";
              script.domain = "www.chatbase.co";
              document.body.appendChild(script);
            };
            
            if (document.readyState === "complete") {
              onLoad();
            } else {
              window.addEventListener("load", onLoad);
            }
          })();
        `;
        document.body.appendChild(scriptEl);
      };

      // Run the script injection
      injectScript();

      // Cleanup function
      return () => {
        // Remove any Chatbase elements when the component unmounts
        document.querySelectorAll('[class*="chatbase"]').forEach((el) => {
          el.remove();
        });
        document.getElementById("y8recvp5I7IIxSzOOlhkt")?.remove();
      };
    }
  }, [user, userVerification]); // Re-run if user or verification changes

  const handleClick = () => {
    // Open the chat when our button is clicked
    if (window.chatbase?.open) {
      window.chatbase.open();
    }
  };

  return (
    <FloatingButton
      type="primary"
      style={{ backgroundColor: "#0084ff" }}
      icon={<CommentOutlined />}
      onClick={handleClick}
    />
  );
};
